import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.observability';
import {
  PaywallRemoveCouponDoc,
  PaywallSubscriptionPurchaseMutation,
  PreviewMediaPurchaseDoc,
  PurchaseMediaDoc,
  PurchaseMediaMutation,
} from '@minecraft.graphql-operations';
import { gqlBillingInfo } from '@minecraft.graphql-types';
import { publishPurchaseFlowEvent } from '@blocs.marketing-events';
import { useUserContext } from '@blocs.auth';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { UsePayment } from './types';
import { PaywallLocation } from './enums/PaywallLocation';
import { PlanType } from './usePlans';
import { MediaPurchase } from './enums/plans';
import { parsePaymentsError } from './helpers/parseApolloError';
import { PaywallEvents } from './enums/events';

export type PurchaseMedia = {
  cardType: PurchaseMediaMutation['purchaseMedia']['paymentDetails']['cardType'];
  currency: PurchaseMediaMutation['purchaseMedia']['currency'];
  email: PurchaseMediaMutation['purchaseMedia']['email'];
  invoiceId: PurchaseMediaMutation['purchaseMedia']['invoiceId'];
  lastFour: PurchaseMediaMutation['purchaseMedia']['paymentDetails']['lastFour'];
  total: PurchaseMediaMutation['purchaseMedia']['total'];
  items: PurchaseMediaMutation['purchaseMedia']['items'];
};

type UsePurchaseMediaProps = {
  selectedPlan: PlanType;
  selectedPaymentOption?: gqlBillingInfo;
  paywallLocation: PaywallLocation;
  setCompletedPurchase?: UsePayment['setCompletedPurchase'];
  setCompletePurchaseError?: UsePayment['setCompletePurchaseError'];
  isAnnualRenewalDue?: boolean;
  isAnnual?: boolean;
  mediaIds?: number[];
  onNext: () => void;
};

export const usePurchaseMedia = ({
  selectedPlan,
  selectedPaymentOption,
  paywallLocation,
  setCompletedPurchase = () => null,
  setCompletePurchaseError = () => null,
  mediaIds,
  onNext,
}: UsePurchaseMediaProps) => {
  const { showMessage } = useGlobalNotification();
  const [currentCouponCode, setCurrentCouponCode] = useState<string>();
  const [applyCouponError, setApplyCouponError] = useState<string>();
  const { refreshTokens, subscription } = useUserContext();
  const { t } = useTranslation();
  const isAlacartePurchaseEnabled = useFeatureExperiment(FEATURE_FLAGS.ALACARTE_MEDIA_PURCHASE);
  const paymentOptionId = selectedPaymentOption?.id ?? subscription?.billingInfoId;
  const selectedPlanBillingCode = selectedPlan?.billingCode;

  const {
    data: previewMediaPurchaseData,
    loading: previewMediaPurchaseLoading,
    error: previewMediaPurchaseError,
  } = useQuery(PreviewMediaPurchaseDoc, {
    variables: {
      input: {
        billingInfoId: String(paymentOptionId),
        mediaIds,
        couponCode: currentCouponCode,
      },
    },
    fetchPolicy: 'network-only',
    skip: !isAlacartePurchaseEnabled || !paymentOptionId || !mediaIds?.length,
  });

  const [removeCouponMutation, { loading: removeCouponLoading }] = useMutation(PaywallRemoveCouponDoc, {
    refetchQueries: [
      {
        query: PreviewMediaPurchaseDoc,
        variables: {
          input: {
            billingInfoId: String(paymentOptionId),
            mediaIds,
            couponCode: currentCouponCode,
          },
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const [
    completePurchaseMediaMutation,
    {
      error: completePurchaseMediaErrorFromNetwork,
      data: completePurchaseMediaData,
      loading: completePurchaseMediaLoading,
    },
  ] = useMutation(PurchaseMediaDoc, {
    update: (cache, { data }) => {
      if (!data?.purchaseMedia) {
        return;
      }
      cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'searchArtistMedia',
      });

      cache.gc();
    },
  });

  useEffect(() => {
    setCurrentCouponCode(undefined);
    setCompletePurchaseError(undefined);
    setApplyCouponError(undefined);
  }, [selectedPlanBillingCode]);

  const applyCoupon = useCallback(
    ({ couponCode }: { couponCode: string }) => {
      if (currentCouponCode !== couponCode) {
        setApplyCouponError(undefined);
      }
      setCurrentCouponCode(couponCode);
    },
    [currentCouponCode, setCurrentCouponCode]
  );

  const removeCoupon = async () => {
    setApplyCouponError(undefined);
    try {
      await removeCouponMutation();
      setCurrentCouponCode(undefined);
    } catch (err) {
      captureExceptionEvent(err);
    }
  };

  useEffect(() => {
    if (!previewMediaPurchaseError && !previewMediaPurchaseLoading && previewMediaPurchaseData && currentCouponCode) {
      const coupon =
        previewMediaPurchaseData.previewMediaPurchase.coupon?.code?.toLowerCase() === currentCouponCode?.toLowerCase();

      if (!coupon) {
        setApplyCouponError(t('common:paywall.errors.INVALID_COUPON_CODE'));
      } else {
        removeCouponMutation()
          .then(() => undefined)
          .catch(() => undefined);
        setApplyCouponError(undefined);
      }
    } else if (previewMediaPurchaseError) {
      const error = parsePaymentsError(previewMediaPurchaseError);
      const errorString = t(`common:paywall.errors.${error}`);
      setApplyCouponError(errorString);
    } else {
      setApplyCouponError(undefined);
    }
  }, [
    currentCouponCode,
    t,
    previewMediaPurchaseError,
    previewMediaPurchaseLoading,
    previewMediaPurchaseData,
    removeCouponMutation,
  ]);

  const previewMediaPurchase = previewMediaPurchaseData?.previewMediaPurchase;

  useEffect(() => {
    if (previewMediaPurchase && selectedPlan && !completePurchaseMediaData) {
      const items = [selectedPlan].map((plan) => ({
        name: `${plan.billingCode}`,
        code: `${plan.billingCode}`,
        price: Number(plan.price),
      }));
      publishPurchaseFlowEvent(PaywallEvents.beginCheckout, {
        location: paywallLocation,
        purchase: {
          items,
          total: previewMediaPurchase?.total,
          currency: previewMediaPurchase?.currency,
        },
      });
    }
  }, [selectedPlan]);

  const completePurchaseMedia = async () => {
    try {
      setCompletedPurchase({ loading: true, data: null });

      const response = await completePurchaseMediaMutation({
        variables: {
          input: { billingInfoId: String(paymentOptionId), mediaIds, couponCode: currentCouponCode },
        },
      });
      const purchase = response?.data?.purchaseMedia;
      if (purchase) {
        if (selectedPlan) {
          const items = purchase?.items
            ?.filter(({ price }) => price > 0)
            .map((item) => ({ ...item, code: MediaPurchase.alacarte }));

          publishPurchaseFlowEvent(PaywallEvents.alacarteMediaPurchaseMedia, {
            location: paywallLocation,
            purchase: {
              items,
              total: purchase.total,
              currency: purchase.currency,
            },
            transactionId: purchase.invoiceId,
            currentPlan: subscription?.plan?.billingCode,
          });
        }
        await refreshTokens();
        setCompletedPurchase({
          loading: false,
          data: response?.data?.purchaseMedia as unknown as PaywallSubscriptionPurchaseMutation['purchaseSubscription'],
        });
        showMessage({
          color: 'success',
          message: t('common:paywall.payment.success'),
        });
        onNext();
      } else {
        setCompletedPurchase({
          loading: false,
          data: null,
        });
        setCompletePurchaseError(t('common:paywall.errors.MEDIA_PURCHASE_ERROR'));
        throw new Error(t('common:paywall.errors.MEDIA_PURCHASE_ERROR'));
      }
    } catch (err) {
      captureExceptionEvent(err);
      throw err;
    }
  };

  return {
    previewMediaPurchase,
    previewMediaPurchaseLoading,
    previewMediaPurchaseError,
    completePurchaseMedia,
    completePurchaseMediaLoading,
    completePurchaseMediaErrorFromNetwork,
    completePurchaseMediaData,
    currencyCode: previewMediaPurchase?.currency?.code,
    purchaseMediaCouponLoading: previewMediaPurchaseLoading || removeCouponLoading,
    applyPurchaseMediaCoupon: applyCoupon,
    removePurchaseMediaCoupon: removeCoupon,
    applyPurchaseMediaCouponError: applyCouponError,
  };
};
